import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { LessonFilter } from '@/services/syllabusService';
import {
    fetchAiLessonsRequest,
    fetchLessonByIdRequest,
    setFilters,
    setCurrentPage,
    setLoading,
    setError,
    clearError,
    resetState,
} from '../store/slices/syllabusSlice';

export const useSyllabus = () => {
    const dispatch = useAppDispatch();
    const {
        lessons,
        skillCounts,
        currentLesson,
        totalLessons,
        currentPage,
        pageSize,
        filters,
        isLoading,
        error,
    } = useAppSelector((state) => state.syllabus);

    // Saga actions
    const fetchAiLessons = useCallback((lessonFilters?: LessonFilter) => {
        const finalFilters = { ...filters, ...lessonFilters };
        dispatch(fetchAiLessonsRequest(finalFilters));
    }, [dispatch, filters]);

    const fetchLessonById = useCallback((id: number) => {
        dispatch(fetchLessonByIdRequest(id));
    }, [dispatch]);

    // Local actions
    const updateFilters = useCallback((newFilters: LessonFilter) => {
        dispatch(setFilters(newFilters));
    }, [dispatch]);

    const updateCurrentPage = useCallback((page: number) => {
        dispatch(setCurrentPage(page));
    }, [dispatch]);

    const updateLoading = useCallback((loading: boolean) => {
        dispatch(setLoading(loading));
    }, [dispatch]);

    const updateError = useCallback((errorMessage: string | null) => {
        dispatch(setError(errorMessage));
    }, [dispatch]);

    const handleClearError = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    const handleResetState = useCallback(() => {
        dispatch(resetState());
    }, [dispatch]);

    // Computed values
    const totalPages = Math.ceil(totalLessons / pageSize);

    const hasNextPage = currentPage < totalPages;
    const hasPreviousPage = currentPage > 1;

    const goToNextPage = useCallback(() => {
        if (hasNextPage) {
            const nextPage = currentPage + 1;
            updateCurrentPage(nextPage);
            fetchAiLessons({ ...filters, page: nextPage });
        }
    }, [hasNextPage, currentPage, updateCurrentPage, fetchAiLessons, filters]);

    const goToPreviousPage = useCallback(() => {
        if (hasPreviousPage) {
            const prevPage = currentPage - 1;
            updateCurrentPage(prevPage);
            fetchAiLessons({ ...filters, page: prevPage });
        }
    }, [hasPreviousPage, currentPage, updateCurrentPage, fetchAiLessons, filters]);

    const goToPage = useCallback((page: number) => {
        if (page >= 1 && page <= totalPages) {
            updateCurrentPage(page);
            fetchAiLessons({ ...filters, page });
        }
    }, [totalPages, updateCurrentPage, fetchAiLessons, filters]);

    return {
        // State
        lessons,
        skillCounts,
        currentLesson,
        totalLessons,
        currentPage,
        pageSize,
        filters,
        isLoading,
        error,

        // Computed
        totalPages,
        hasNextPage,
        hasPreviousPage,

        // Actions
        fetchAiLessons,
        fetchLessonById,
        updateFilters,
        updateCurrentPage,
        updateLoading,
        updateError,
        handleClearError,
        handleResetState,

        // Navigation
        goToNextPage,
        goToPreviousPage,
        goToPage,
    };
}; 